import { getPreferenceFromCache, setPreference, deletePreference, type PreferenceNamespace } from "./PreferenceStore";

type TokenKeys = "token" | "refresh-token";
type ImpersonationKey = "connectedAs";
type LanguageSelectorKey = "language";
type SelectedCompany = "company";
type CompanyInfoBox = "displayRegulatoryInfoBox";
type SidebarStatus = "sidebarStatus";
type LastSelectedCompanyScore = "lastSelectedCompanyScore";
type CatalogViewKey = "catalogView";

type HideEngagedActions = "hideEngagedActions";

type LocalStorageKeys =
    | TokenKeys
    | ImpersonationKey
    | LanguageSelectorKey
    | SelectedCompany
    | CompanyInfoBox
    | SidebarStatus
    | LastSelectedCompanyScore
    | CatalogViewKey
    | HideEngagedActions
    | (string & {});

type PreferenceMappedKey =
    | CompanyInfoBox
    | SidebarStatus
    | LastSelectedCompanyScore
    | CatalogViewKey
    | HideEngagedActions;

const PREFERENCE_KEY_MAP: Record<PreferenceMappedKey, { namespace: PreferenceNamespace; preferenceKey: string }> = {
    sidebarStatus: { namespace: "ui", preferenceKey: "sidebarStatus" },
    catalogView: { namespace: "ui", preferenceKey: "catalogView" },
    displayRegulatoryInfoBox: { namespace: "ui", preferenceKey: "displayRegulatoryInfoBox" },
    lastSelectedCompanyScore: { namespace: "ui", preferenceKey: "lastSelectedCompanyScore" },
    hideEngagedActions: { namespace: "ui", preferenceKey: "hideEngagedActions" },
};

function isPreferenceMappedKey(key: string): key is PreferenceMappedKey {
    return key in PREFERENCE_KEY_MAP;
}

export class LocalStorage {
    static getItem(key: LocalStorageKeys): string | null {
        if (isPreferenceMappedKey(key)) {
            const mapping = PREFERENCE_KEY_MAP[key];
            const cached = getPreferenceFromCache<string>(mapping.namespace, mapping.preferenceKey);
            if (cached !== null) return cached;
        }

        return localStorage.getItem(key);
    }

    static setItem(key: LocalStorageKeys, value: string) {
        localStorage.setItem(key, value);

        if (isPreferenceMappedKey(key)) {
            const mapping = PREFERENCE_KEY_MAP[key];
            setPreference(mapping.namespace, mapping.preferenceKey, value);
        }
    }

    static deleteItem(key: LocalStorageKeys) {
        localStorage.removeItem(key);

        if (isPreferenceMappedKey(key)) {
            const mapping = PREFERENCE_KEY_MAP[key];
            deletePreference(mapping.namespace, mapping.preferenceKey);
        }
    }
}
